/*
 * Copyright (c) 2008-present Sonatype, Inc.
 *
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/pro/attributions
 * Sonatype and Sonatype Nexus are trademarks of Sonatype, Inc. Apache Maven is a trademark of the Apache Foundation.
 * M2Eclipse is a trademark of the Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

import axios from 'axios';
import {assign} from 'xstate';

import {ExtJS, FormUtils, ValidationUtils} from '@sonatype/nexus-ui-plugin';

import UIStrings from "../../../../constants/UIStrings";

const PLACEHOLDER = '#~NXRM~PLACEHOLDER~PASSWORD~#';
const HOSTED_REPOSITORIES = '/service/rest/internal/ui/repositories?type=hosted';
const REPLICATION_URL = (id) => `/service/rest/beta/replication/connection/${id}`;
const TEST_URL = '/service/rest/internal/ui/replication/test';

export default FormUtils.buildFormMachine({
  id: 'ReplicationFormMachine',
  config: (config) => ({
    ...config,
    context: {
      ...config.context,
      sourceRepositories: [],
      destinationRepositories: []
    },
    states: {
      ...config.states,
      loaded: {
        ...config.states.loaded,
        on: {
          ...config.states.loaded.on,
          UPDATE_TEST_CONNECTION: {
            target: 'loaded',
            actions: ['clearDestinationRepositoryName', 'closeSuccessNotification', 'update'],
            internal: false
          },
          TEST_REPOSITORY_CONNECTION: {
            target: 'testingConnection'
          },
          CLOSE_SUCCESS_NOTIFICATION: {
            actions: ['closeSuccessNotification']
          }
        }
      },
      testingConnection: {
        invoke: {
          src: 'testRepositoryConnection',
          onDone: {
            target: 'loaded',
            actions: ['setDestinationRepositories', 'showSuccessNotification']
          },
          onError: {
            target: 'loaded'
          }
        }
      }
    }
  })
}).withConfig({
  actions: {
    clearDestinationRepositoryName: assign({
      destinationRepositories: () => [],
      data: ({data}) => ({
        ...data,
        destinationRepositoryName: ''
      })
    }),

    setData: assign({
      sourceRepositories: (_, event) => event.data[0].data,
      data: (_, event) => event.data[1].data,
      pristineData: (_, event) => event.data[1].data,
      destinationRepositories: (_, event) => event.data[2].data
    }),

    setDestinationRepositories: assign({
      destinationRepositories: (_, event) => event.data.data
    }),

    onLoadedEntry: assign({
      data: ({pristineData, data}) => ({
        ...data,
        destinationInstancePassword: (ValidationUtils.notBlank(pristineData.id) && !data.destinationInstancePassword) ?
            PLACEHOLDER :
            data.destinationInstancePassword
      })
    }),

    showSuccessNotification: assign({
      showSuccessNotification: () => true
    }),

    closeSuccessNotification: assign({
      showSuccessNotification: () => false
    }),

    validate: assign({
      validationErrors: ({data}) => ({
        name: ValidationUtils.validateNotBlank(data.name),
        sourceRepositoryName: ValidationUtils.validateNotBlank(data.sourceRepositoryName),
        destinationInstanceUrl: ValidationUtils.validateIsUrl(data.destinationInstanceUrl),
        destinationInstanceUsername: ValidationUtils.validateNotBlank(data.destinationInstanceUsername),
        destinationInstancePassword: ValidationUtils.validateNotBlank(data.destinationInstancePassword),
        destinationRepositoryName: ValidationUtils.validateNotBlank(data.destinationRepositoryName)
      })
    })
  },

  services: {
    fetchData: ({pristineData}) => {
      if (ValidationUtils.notBlank(pristineData.id)) {
        return axios.all([
          axios.get(HOSTED_REPOSITORIES),
          axios.get(REPLICATION_URL(pristineData.id)),
          axios.post(TEST_URL, {id: pristineData.id})
        ]);
      }
      else {
        return axios.all([
          axios.get(HOSTED_REPOSITORIES),
          Promise.resolve({
            data: {
              id: '',
              name: '',
              sourceRepositoryName: '',
              destinationInstanceUrl: '',
              destinationInstanceUsername: '',
              destinationInstancePassword: '',
              destinationRepositoryName: '',
              contentRegexes: null
            }
          }),
          Promise.resolve({data: []})
        ]);
      }
    },

    testRepositoryConnection: ({data}) => {
      const {destinationInstanceUrl, destinationInstanceUsername, destinationInstancePassword} = data;
      return axios.post(TEST_URL, {destinationInstanceUrl, destinationInstanceUsername, destinationInstancePassword});
    },

    saveData: ({data, pristineData}) => {
      const replicationUrl = REPLICATION_URL(pristineData.id);

      if (data.destinationInstancePassword === PLACEHOLDER) {
        data.destinationInstancePassword = null;
      }

      if (ValidationUtils.notBlank(pristineData.id)) {
        return axios.put(replicationUrl, data);
      }
      else {
        return axios.post(replicationUrl, data);
      }
    },

    confirmDelete: ({data}) => ExtJS.requestConfirmation({
      title: UIStrings.REPLICATION.MESSAGES.CONFIRM_DELETE.TITLE,
      message: UIStrings.REPLICATION.MESSAGES.CONFIRM_DELETE.MESSAGE(data.name),
      yesButtonText: UIStrings.REPLICATION.MESSAGES.CONFIRM_DELETE.YES,
      noButtonText: UIStrings.REPLICATION.MESSAGES.CONFIRM_DELETE.NO
    }),

    delete: ({pristineData}) => {
      return axios.delete(REPLICATION_URL(pristineData.id));
    }
  }
});
